import { Outlet } from "react-router-dom";
import { Sigle, deNom, useBranding } from "../../branding";
import { cx } from "../../components";
import { useHeroTimeline } from "../../motion";
import { VitrineLedger } from "../vitrine/VitrineLedger";
import vitrine from "../vitrine/Vitrine.module.css";
import styles from "./Auth.module.css";

export type AuthLayoutProps = {
  /** Carte large, pour un formulaire à deux colonnes (l'inscription). */
  wide?: boolean;
};

/**
 * Coquille des écrans publics : le formulaire à droite, et à gauche un panneau
 * aux couleurs de la plateforme où se rejoue le grand livre — ce qu'on
 * retrouvera une fois connecté, montré avant d'entrer. Pas de navigation : il
 * n'y a nulle part où aller sans session.
 *
 * Toujours à l'accent de l'instance : celui d'une entreprise ne s'applique
 * qu'à son espace, une fois connecté. En largeur réduite, le panneau disparaît
 * et le formulaire reste seul, sur un fond teinté.
 *
 * Le panneau est `inert` : décoratif, il ne doit ni prendre le focus (son
 * bouton « rejouer ») ni être lu avant le formulaire.
 */
export function AuthLayout({ wide = false }: AuthLayoutProps) {
  const { product_name, esn_name } = useBranding();
  // L'arrivée est orchestrée comme celle de la vitrine : l'accroche ligne par
  // ligne, puis le grand livre et la carte du formulaire ensemble.
  const scene = useHeroTimeline<HTMLDivElement>({ stagger: 0.1, delay: 0.05 });
  return (
    <div ref={scene} className={styles.fond}>
      <aside className={cx(vitrine.sombre, styles.panneau)} inert>
        <div className={styles.panneauTextes}>
          <p className={styles.panneauSurtitre} data-anim="hero">
            {product_name}
          </p>
          <p className={styles.panneauTitre}>
            <span data-anim="hero">Vos prestations, payées d'avance.</span>{" "}
            <span data-anim="hero">Chaque token, tracé.</span>
          </p>
          <p className={styles.panneauChapo} data-anim="hero">
            L'espace client {deNom(esn_name)} : votre solde, vos projets et vos consultants, au token près.
          </p>
        </div>
        <div className={styles.panneauVisuel} data-anim="hero-visual">
          <VitrineLedger />
        </div>
      </aside>
      <div className={styles.zoneFormulaire}>
        {/* Le décor du formulaire : trois halos de couleur qui dérivent
            lentement derrière la carte, aux teintes de la marque et des
            seaux. Purement décoratif. */}
        <div className={styles.aurore} aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <main id="contenu" className={cx(styles.carte, wide && styles.carteLarge)} data-anim="hero-visual">
          <div className={styles.marque}>
            <Sigle />
            <span className={styles.nom}>{product_name}</span>
          </div>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
